import React, { Fragment } from "react";
import _ from "lodash/fp";
import {
  AppBar,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from "@mui/material";
import { useForm } from "react-hook-form";

import { ControlledFormCheckbox } from "../../../../components";

const DynamicRemoveLabelsTagDialog = ({
  create,
  items = [],
  onClose,
  openState: [open, setOpen],
  ranges = [],
  readonly,
  tag = {},
}) => {
  const { range, ...formValues } = tag;

  const { control, getValues, watch } = useForm({
    defaultValues: {
      ...formValues,
      items: items.map(({ id }) => (tag.items.includes(id) ? id : false)),
      labels: ["paragraph", "sentence"].reduce((acc, type) => {
        return {
          ...acc,
          [type]: ranges
            .filter((range) => range.type === type)
            .map((range) =>
              tag.labels.findIndex(
                (o) => o.begin === range.begin && o.end === range.end
              ) > -1
                ? range.begin
                : false
            ),
        };
      }, {}),
    },
  });
  const [selectedItems, selectedLabels] = watch(["items", "labels"]);

  const handleCancel = () => {
    setOpen(false);
  };
  const handleClose = () => {
    onClose(create)({
      ...getValues(),
      items: selectedItems.filter(Boolean),
      labels: ranges
        .map(({ begin, end, type }) =>
          selectedLabels[type].includes(begin) ? { begin, end } : false
        )
        .filter(Boolean),
    });
    setOpen(false);
  };

  return (
    <Dialog maxWidth="md" onClose={handleClose} open={open}>
      <DialogTitle>
        {(readonly && "View") || (create ? "Create" : "Update")} Remove
        Paragraph/Sentence Numbers Tag
      </DialogTitle>
      <DialogContent
        sx={{
          display: "flex",
          flexDirection: "row",
          maxHeight: "450px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            flexGrow: 1,
            minWidth: "15em",
            overflowY: "auto",
          }}
        >
          <AppBar color="transparent" elevation={1} position="static">
            <Typography variant="h5">Items</Typography>
          </AppBar>
          <List sx={{ overflowY: "auto" }}>
            {items.map(({ id, name }, index) => (
              <ListItem dense key={id}>
                <ListItemIcon>
                  <ControlledFormCheckbox
                    checkboxProps={{ size: "small" }}
                    control={control}
                    defaultValue={tag?.items?.includes(id) ? id : false}
                    id={`items.${index}`}
                    label=""
                    name={`items.${index}`}
                    readonly={readonly}
                    value={id}
                  />
                </ListItemIcon>
                <ListItemText id={id} primary={name} />
              </ListItem>
            ))}
          </List>
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            flexGrow: 1,
            marginLeft: 2,
            overflowY: "auto",
          }}
        >
          {["paragraph", "sentence"].map((type) => {
            if (!ranges.some((o) => o.type === type)) return;
            return (
              <Fragment key={type}>
                <AppBar
                  color="transparent"
                  elevation={1}
                  position="static"
                  sx={{ marginBottom: 2 }}
                >
                  <Typography variant="h5">
                    {_.capitalize(type)} Numbers
                  </Typography>
                </AppBar>
                <List sx={{ overflowY: "auto" }}>
                  {ranges
                    .filter((o) => {
                      return o.type === type;
                    })
                    .map(({ begin, content, end }, index) => {
                      return (
                        <ListItem dense key={begin}>
                          <ListItemIcon>
                            <ControlledFormCheckbox
                              checkboxProps={{ size: "small" }}
                              control={control}
                              defaultValue={
                                tag?.labels?.some((o) => {
                                  return o.begin === begin && o.end === end;
                                })
                                  ? begin
                                  : false
                              }
                              id={`labels.${type}.${index}`}
                              label=""
                              name={`labels.${type}.${index}`}
                              readonly={readonly}
                              value={begin}
                            />
                          </ListItemIcon>
                          <ListItemText id={begin} primary={content} />
                        </ListItem>
                      );
                    })}
                </List>
              </Fragment>
            );
          })}
        </Box>
      </DialogContent>
      <DialogActions>
        {readonly && (
          <Button onClick={handleCancel} variant="text">
            Close
          </Button>
        )}
        {!readonly && (
          <>
            <Button onClick={handleCancel} variant="text">
              Cancel
            </Button>
            <Button
              color="primary"
              disabled={
                !selectedItems.filter(Boolean).length ||
                !["paragraph", "sentence"].reduce(
                  (acc, type) => [
                    ...acc,
                    ...selectedLabels[type].filter(Boolean),
                  ],
                  []
                ).length
              }
              onClick={handleClose}
              variant="contained"
            >
              {create ? "Create" : "Update"}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export { DynamicRemoveLabelsTagDialog };
